import { ArrowLeft, ArrowRight, Clock3, Headphones, ShieldCheck, Smartphone, UsersRound } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, type CatalogItem } from "../api";
import { ErrorState, LoadingState } from "../components/Feedback";
import { ExperienceCover } from "../components/ExperienceCover";
import { RatingSummary } from "../components/RatingSummary";

const deviceCopy = {
  one: "Un teléfono",
  multiple: "Un teléfono por persona",
  either: "Uno o varios teléfonos"
};

export function ProductPage() {
  const { slug = "" } = useParams();
  const [item, setItem] = useState<CatalogItem>();
  const [error, setError] = useState("");

  useEffect(() => {
    api.product(slug)
      .then((product) => {
        setItem(product);
        api.track("product_viewed", crypto.randomUUID(), { product_id: product.id });
      })
      .catch((reason) => setError(reason instanceof Error ? reason.message : "No encontramos la experiencia."));
  }, [slug]);

  if (error) return <ErrorState message={error} />;
  if (!item) return <LoadingState label="Preparando la ficha…" />;

  return (
    <div className={`product-page product-${item.cover.accent}`}>
      <Link className="back-link" to="/catalogo"><ArrowLeft aria-hidden="true" /> Volver al catálogo</Link>
      <div className="product-lead">
        <div className="product-cover-wrap"><ExperienceCover item={item} priority /></div>
        <div className="product-copy">
          <h1>{item.title}</h1>
          <p className="product-tagline">{item.tagline}</p>
          <RatingSummary summary={item.ratingSummary} expanded />
          <p className="product-description">{item.description}</p>
          <div className="product-facts" aria-label="Datos de la experiencia">
            <span><Clock3 aria-hidden="true" /> {item.estimatedMinutes} minutos</span>
            <span><UsersRound aria-hidden="true" /> {item.minPlayers === item.maxPlayers ? item.minPlayers : `${item.minPlayers}–${item.maxPlayers}`} personas</span>
            <span><Smartphone aria-hidden="true" /> {deviceCopy[item.recommendedDevices]}</span>
            <span><Headphones aria-hidden="true" /> Funciona sin audio</span>
          </div>
          <div className="purchase-line">
            <div><strong>${item.priceMxn}</strong><span>MXN por grupo</span></div>
            <Link className="button button-citron button-large" to={`/checkout/${item.slug}`}>Empezar esta noche <ArrowRight aria-hidden="true" /></Link>
          </div>
          <p className="purchase-note"><ShieldCheck aria-hidden="true" /> Acceso por 90 días. En esta versión, el checkout opera en modo demostración y no realiza cargos.</p>
        </div>
      </div>

      <section className="product-detail-grid">
        <div>
          <h2>Lo que ocurrirá</h2>
          <ol className="experience-sequence">
            <li><span>La sala</span><strong>Crean un enlace y entran sin cuenta.</strong></li>
            <li><span>Las ventanas</span><strong>Cada persona recibe información o decisiones propias.</strong></li>
            <li><span>El cruce</span><strong>La historia cambia con lo que hacen juntos.</strong></li>
            <li><span>El cierre</span><strong>Reciben resultado, recap y un momento para compartir.</strong></li>
          </ol>
        </div>
        <aside className="before-you-play">
          <h2>Antes de jugar</h2>
          <dl>
            <div><dt>Dificultad</dt><dd>{item.difficulty}</dd></div>
            <div><dt>Intensidad</dt><dd>{item.intensity}</dd></div>
            <div><dt>Edad sugerida</dt><dd>{item.contentRating.age}+</dd></div>
            <div><dt>Contenido</dt><dd>{item.contentRating.warnings.length ? item.contentRating.warnings.join(", ") : "sin advertencias adicionales"}</dd></div>
          </dl>
          <p>No se necesitan materiales. Recomendamos batería suficiente, una conexión estable y estar en el mismo espacio.</p>
        </aside>
      </section>
    </div>
  );
}
